/**
 * useCampaignDeepLinkPanel — opens a creator drawer from the typed
 * `?panel` + `?creator` search params.
 *
 *   panel='insights'      -> deepAnalysisCreator (CreatorInsightsDrawer)
 *   panel='brand-safety'  -> deepAnalysisCreator + deepAnalysisTab='brand-safety'
 *                           (the insights drawer focused on its brand-safety tab)
 *   panel='conversation'  -> convCreator         (ReviewThreadDrawer — selection thread)
 *
 * The `creator` param is the platform **username** (e.g. `?creator=mrbeast`),
 * NOT an internal id: usernames are unique per platform, every campaign is a
 * single platform, and a readable username makes the link shareable. Trade-off
 * (accepted): if a creator renames, old links break — rare for known creators.
 *
 * One-way (URL -> store): the effect mirrors the URL into the Zustand store; it
 * never writes the store back to the URL.
 *
 * Off-page relanding: if the creator is not in any loaded tab AND `rowsReady`
 * is true, we call `useResolveCreatorByUsername` to find which tab/pill/page
 * they're on, navigate there (healing the URL), and open the global drawer
 * from the resolved entry — without waiting for the destination page's rows
 * to load.
 *
 * Phase E — cross-tab follow-the-creator:
 *   When a share link's `?tab=` doesn't match the creator's *current* tab
 *   (i.e. the creator isn't in the linked tab's loaded entries), the on-page
 *   effect navigates `?tab=` to where they ARE, mapping the panel value when
 *   destination is `confirmed`:
 *     script-review  → script-finalized
 *     content-review → content-finalized
 *   The destination is chosen from the per-tab entries (post-phase tabs
 *   preferred over `confirmed` because the live in-workflow surface is more
 *   relevant when the creator's post hasn't been frozen yet).
 *
 *   Crucially, when the user is *actively* on the linked tab (e.g. they just
 *   clicked Review on Content tab and the row IS in content.data), no
 *   redirect fires — only stale share-links get redirected.
 *
 * The `relandedFor` ref prevents a navigate→refetch→re-resolve loop: once
 * we've relanded for a given username we skip future resolve effects for it.
 * The `redirectedFor` ref prevents the on-page redirect from ping-ponging.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useCampaignPageSizeParam } from './useCampaignPageSizeParam';
import { markAtomicRosterNav } from './hooks/rosterNavIntent';
import { toast } from 'sonner';
import {
  useCampaignUiStore,
  useResolveCreatorByUsername,
  type DeepAnalysisTab,
  type RosterRowRef,
} from '@discovr/features';
import { DEFAULT_CAMPAIGN_TAB, type CampaignSearch } from './search';
import type { CampaignPipeline } from '@discovr/types';
import { resolvePipeline } from './utils/campaignPipeline';

const DEEP_ANALYSIS_TABS: readonly DeepAnalysisTab[] = [
  'overview',
  'detailed',
  'brand-safety',
  'creator-fit',
];

const isDeepAnalysisTab = (value: string | undefined): value is DeepAnalysisTab =>
  value !== undefined && (DEEP_ANALYSIS_TABS as readonly string[]).includes(value);

/**
 * Per-tab roster entries loaded by useCampaignDetail. Used by the cross-tab
 * redirect (Phase E) to determine where a creator currently lives across the
 * five paginated roster tabs.
 */
export interface CampaignDeepLinkRowsByTab {
  creators: ReadonlyArray<RosterRowRef>;
  finalized: ReadonlyArray<RosterRowRef>;
  scripts: ReadonlyArray<RosterRowRef>;
  content: ReadonlyArray<RosterRowRef>;
  live: ReadonlyArray<RosterRowRef>;
}

type TabKey = keyof CampaignDeepLinkRowsByTab;
type DestinationTab = NonNullable<CampaignSearch['tab']>;

/**
 * Maps an internal per-tab key to the URL `?tab=` enum value. The roster
 * data slot for "live-content" tab is `live`; the URL value is `live-content`.
 */
const TAB_KEY_TO_URL: Record<TabKey, DestinationTab> = {
  creators: 'creators',
  finalized: 'confirmed',
  scripts: 'scripts',
  content: 'content',
  live: 'live-content',
};

const URL_TO_TAB_KEY: Partial<Record<string, TabKey>> = {
  creators: 'creators',
  confirmed: 'finalized',
  scripts: 'scripts',
  content: 'content',
  'live-content': 'live',
};

/**
 * The workflow tab that OWNS each in-workflow review panel. When the URL's
 * `?tab=` already equals the panel's home tab, that tab's local deep-link
 * reader (useCampaignReviewDeepLink — pill-scoped, freshly fetched) owns
 * resolution: it forces the right bucket and opens the dialog itself. The
 * global hook must stand down entirely there — its rowsByTab snapshots are
 * page-1/limit-20 and minutes stale, so "creator not in rows.scripts" is NOT
 * evidence they're past the scripts phase (and rows.finalized contains every
 * finalized creator forever, so its presence proves nothing either). Without
 * this gate, clicking Review right after a script upload bounced the panel to
 * tab=confirmed&panel=script-finalized. Cross-tab links (?tab= elsewhere)
 * keep the Phase E redirect.
 */
const REVIEW_PANEL_HOME_TAB: Partial<Record<string, DestinationTab>> = {
  'script-review': 'scripts',
  'content-review': 'content',
};

/**
 * Look up the creator's row on every loaded tab. Used by destinationForPanel
 * which needs the row (specifically `row.status`) to compute the correct pill
 * + panel pair — e.g. a script-review URL for a creator whose post is in
 * script_pending status must redirect to pill=pending with panel cleared (the
 * script hasn't been uploaded — there's nothing to review).
 */
type RowsByCreator = Partial<Record<TabKey, RosterRowRef>>;

function findCreatorRows(
  username: string,
  byTab: CampaignDeepLinkRowsByTab,
): RowsByCreator {
  const out: RowsByCreator = {};
  (['creators', 'finalized', 'scripts', 'content', 'live'] as TabKey[]).forEach((tab) => {
    const row = byTab[tab].find((r) => r.platform?.username === username);
    if (row) out[tab] = row;
  });
  return out;
}

function rowStatus(row: RosterRowRef | undefined): string | undefined {
  const raw = (row as unknown as { status?: unknown } | undefined)?.status;
  return typeof raw === 'string' ? raw : undefined;
}

type PanelValue = NonNullable<CampaignSearch['panel']>;

/**
 * Per-panel destination resolution. Returns the (tab, pill, panel) the link
 * should land on, computed from where the creator currently lives in the
 * loaded data AND their row status. `panel` is `undefined` when the link is
 * semantically invalid for the creator's current state (e.g. script-review on
 * a script_pending creator — no script to review yet, just route to pending).
 *
 *   script-review / content-review:
 *     - status matches (script_review / content_review) → keep panel, pill=review
 *     - status is *_pending → clear panel, pill=pending (link routes to the
 *       correct pill but no dialog opens)
 *     - creator past the workflow tab (only in finalized) → confirmed + the
 *       finalized counterpart panel (script-finalized / content-finalized)
 *
 *   finalized read-only (script-finalized / content-finalized):
 *     Confirmed tab only.
 *
 *   global drawers (insights / brand-safety / conversation):
 *     prefer Confirmed (the "all approved creators" home), else Creators.
 *
 *   video popup:
 *     prefer Live Content, then Content, then Confirmed.
 */
export function destinationForPanel(
  panel: PanelValue,
  rows: RowsByCreator,
  campaignPipeline?: CampaignPipeline | null,
): { tab: DestinationTab; panel: PanelValue | undefined; pill?: string } | null {
  const pipeline = resolvePipeline(campaignPipeline);
  // A lane the campaign doesn't run has no tab to land on; its rows are only
  // reachable through the Confirmed tab.
  if (!pipeline.script_enabled) rows = { ...rows, scripts: undefined };
  if (!pipeline.content_enabled) rows = { ...rows, content: undefined };
  switch (panel) {
    case 'script-review': {
      if (rows.scripts) {
        const status = rowStatus(rows.scripts);
        if (status === 'script_review') return { tab: 'scripts', panel: 'script-review', pill: 'review' };
        if (status === 'script_pending') return { tab: 'scripts', panel: undefined, pill: 'pending' };
        // Unknown status — treat as broken link, route to scripts list.
        return { tab: 'scripts', panel: undefined };
      }
      if (rows.finalized) return { tab: 'confirmed', panel: 'script-finalized' };
      return null;
    }
    case 'content-review': {
      if (rows.content) {
        const status = rowStatus(rows.content);
        if (status === 'content_review') return { tab: 'content', panel: 'content-review', pill: 'review' };
        if (status === 'content_pending') return { tab: 'content', panel: undefined, pill: 'pending' };
        return { tab: 'content', panel: undefined };
      }
      if (rows.finalized) return { tab: 'confirmed', panel: 'content-finalized' };
      return null;
    }
    case 'script-finalized':
    case 'content-finalized':
      return rows.finalized ? { tab: 'confirmed', panel } : null;
    case 'insights':
    case 'brand-safety':
    case 'conversation': {
      const target = panel === 'brand-safety' && !pipeline.brand_safety_enabled ? 'insights' : panel;
      if (rows.finalized) return { tab: 'confirmed', panel: target };
      if (rows.creators) return { tab: 'creators', panel: target };
      return null;
    }
    case 'video':
      if (rows.live) return { tab: 'live-content', panel: 'video' };
      if (rows.content) return { tab: 'content', panel: 'video' };
      if (rows.finalized) return { tab: 'confirmed', panel: 'video' };
      return null;
    default:
      return null;
  }
}

/**
 * Map a roster status to its canonical `pill` value on the Creators tab. Used
 * only when destination is `creators` (the other tabs don't use ?pill the
 * same way).
 */
function rosterPillFromStatus(status: string | undefined): string | undefined {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'accepted_by_brand':
    case 'accepted_by_admin':
      return 'approved';
    case 'rejected_by_brand':
    case 'rejected_by_admin':
    case 'rejected_by_creator':
    case 'removed_by_admin':
      return 'rejected';
    default:
      return undefined;
  }
}

export function useCampaignDeepLinkPanel(
  rowsByTab: CampaignDeepLinkRowsByTab,
  rowsReady: boolean,
) {
  // A creator link with no `?tab` targets the default tab.
  const { tab = DEFAULT_CAMPAIGN_TAB, panel, creator, sub } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const campaignId = useCampaignUiStore((s) => s.campaignId);
  const campaignPipeline = useCampaignUiStore((s) => s.campaignPipeline);
  const setDeepAnalysisCreator = useCampaignUiStore((s) => s.setDeepAnalysisCreator);
  const setDeepAnalysisTab = useCampaignUiStore((s) => s.setDeepAnalysisTab);
  const setConvCreator = useCampaignUiStore((s) => s.setConvCreator);
  const navigate = useNavigate();

  // Aggregate row list — still needed for the off-page resolver gate and the
  // global-drawer setter.
  const allRows = useMemo<ReadonlyArray<RosterRowRef>>(
    () => [
      ...rowsByTab.creators,
      ...rowsByTab.finalized,
      ...rowsByTab.scripts,
      ...rowsByTab.content,
      ...rowsByTab.live,
    ],
    [rowsByTab.creators, rowsByTab.finalized, rowsByTab.scripts, rowsByTab.content, rowsByTab.live],
  );

  // Phase E — look up the creator's row on each tab once so destinationForPanel
  // can read row.status (used to compute the correct pill / panel).
  const creatorRows = useMemo<RowsByCreator>(
    () => (creator ? findCreatorRows(creator, rowsByTab) : {}),
    [creator, rowsByTab],
  );
  const onPageDestination = useMemo(
    () => (panel ? destinationForPanel(panel as PanelValue, creatorRows, campaignPipeline) : null),
    [panel, creatorRows, campaignPipeline],
  );
  const onPage = !!creator && allRows.some((r) => r.platform?.username === creator);

  // Home-tab ownership: the linked tab's local reader resolves the panel with
  // fresh pill-scoped data — no global redirect, no off-page resolve.
  const isOnPanelHomeTab = !!panel && REVIEW_PANEL_HOME_TAB[panel] === tab;

  // Resolver gate: only fire when rows are settled AND we lack enough on-page
  // data to redirect/open. The second clause matters for creators whose data
  // is partially loaded — e.g. they're in live.data but NOT on page 1 of
  // finalized.data — where `destinationForPanel` returns null and the off-page
  // resolver is the only path that can supply the right canonical tab.
  const needResolve =
    !!panel && !!creator && rowsReady && !isOnPanelHomeTab && (!onPage || !onPageDestination);

  // Resolve against the size the destination tab actually renders with,
  // otherwise `page = rank/limit + 1` points somewhere else entirely.
  const [activePageSize] = useCampaignPageSizeParam();
  const resolved = useResolveCreatorByUsername(campaignId, creator, needResolve, activePageSize);

  const relandedFor = useRef<string | null>(null);
  const redirectedFor = useRef<string | null>(null);
  const toastedFor = useRef<string | null>(null);

  // On-page effect (with Phase E cross-tab redirect).
  useEffect(() => {
    if (!panel || !creator) return;
    // The panel's home tab owns resolution — never redirect away from it.
    if (isOnPanelHomeTab) return;
    // No on-page data for the creator OR no resolvable destination from what
    // we have → let the off-page resolver effect handle it.
    const haveData = Object.keys(creatorRows).length > 0;
    if (!haveData || !onPageDestination) return;

    const destination = onPageDestination;
    const linkedTabKey = tab ? URL_TO_TAB_KEY[tab] : undefined;

    // For destination=creators, also derive the right roster-status pill if
    // destination didn't supply one already (e.g. brand-safety link routing).
    const fallbackCreatorsPill =
      destination.tab === 'creators' && destination.pill === undefined
        ? rosterPillFromStatus(rowStatus(creatorRows.creators))
        : undefined;
    const destPill = destination.pill ?? fallbackCreatorsPill;

    const redirectKey = `${creator}|${panel}`;
    const needsRedirect =
      // Tab is wrong, OR panel needs to be remapped / cleared (e.g.
      // script-review → script-finalized for finalized creator, or
      // script-review → undefined for a script_pending creator).
      (linkedTabKey !== undefined && URL_TO_TAB_KEY[tab as string] !== undefined &&
        TAB_KEY_TO_URL[linkedTabKey] !== destination.tab) ||
      (destination.panel ?? null) !== (panel ?? null);

    if (needsRedirect && redirectedFor.current !== redirectKey) {
      redirectedFor.current = redirectKey;

      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          tab: destination.tab,
          ...(destPill !== undefined ? { pill: destPill } : {}),
          panel: destination.panel,
          page: undefined,
        })),
      } as never);
      return;
    }

    // Same-tab path: open the global drawer for insights / brand-safety /
    // conversation. script-finalized / content-finalized / script-review /
    // content-review / video panels are handled by the destination tab's
    // local reader after navigation (no work here).
    const row = allRows.find((r) => r.platform?.username === creator);
    if (!row) return;
    if (panel === 'insights') {
      setDeepAnalysisCreator(row);
      if (isDeepAnalysisTab(sub)) setDeepAnalysisTab(sub);
      else setDeepAnalysisTab('overview');
    } else if (panel === 'brand-safety') {
      setDeepAnalysisCreator(row);
      setDeepAnalysisTab('brand-safety');
    } else if (panel === 'conversation') {
      setConvCreator(row);
    }
  }, [
    panel,
    creator,
    sub,
    tab,
    isOnPanelHomeTab,
    rowsByTab,
    allRows,
    navigate,
    setDeepAnalysisCreator,
    setDeepAnalysisTab,
    setConvCreator,
  ]);

  // Off-page relanding effect.
  useEffect(() => {
    if (!needResolve || !resolved.data || relandedFor.current === creator) return;
    relandedFor.current = creator ?? null;

    const { creator: resolvedRow, tab: resolvedTab, pill, page } = resolved.data;

    // Phase E — map panel based on the resolver's destination tab. The
    // resolver returns ROSTER-status-derived tab (creators / confirmed); the
    // panel-collapse rule applies when destination is Confirmed for the
    // in-workflow review panel values.
    const mappedPanel: PanelValue | undefined =
      panel === 'script-review' && resolvedTab === 'confirmed'
        ? 'script-finalized'
        : panel === 'content-review' && resolvedTab === 'confirmed'
          ? 'content-finalized'
          : (panel as PanelValue | undefined);

    // Declare the atomic {pill, page} intent BEFORE navigating so the tabs
    // hook honors the resolver's page instead of resetting to 1 — value-diffing
    // alone can't tell this apart from a pill click when the resolved page
    // numerically equals the current one.
    markAtomicRosterNav();
    navigate({
      to: '.',
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        tab: resolvedTab,
        pill,
        page: page > 1 ? page : undefined,
        ...(mappedPanel !== panel ? { panel: mappedPanel } : null),
      })),
    } as never);

    if (panel === 'insights') {
      setDeepAnalysisCreator(resolvedRow as unknown as RosterRowRef);
      if (isDeepAnalysisTab(sub)) setDeepAnalysisTab(sub);
    } else if (panel === 'brand-safety') {
      setDeepAnalysisCreator(resolvedRow as unknown as RosterRowRef);
      setDeepAnalysisTab('brand-safety');
    } else if (panel === 'conversation') {
      setConvCreator(resolvedRow as unknown as RosterRowRef);
    }
  }, [
    needResolve,
    resolved.data,
    creator,
    panel,
    sub,
    navigate,
    setDeepAnalysisCreator,
    setDeepAnalysisTab,
    setConvCreator,
  ]);

  // Error effect: creator not found anywhere in this campaign.
  useEffect(() => {
    if (needResolve && resolved.isError && toastedFor.current !== creator) {
      toastedFor.current = creator ?? null;
      toast.error('This creator is no longer in this campaign.');
    }
  }, [needResolve, resolved.isError, creator]);
}
